/**
 * Enums do contrato traduzidos, com o tom visual de cada um.
 *
 * Antes eram constantes com texto em português. Agora são funções do
 * dicionário: o TEXTO muda com o idioma, o TOM não — vermelho é urgência em
 * qualquer língua, e deixar a cor no dicionário abriria a porta para os dois
 * idiomas divergirem em gravidade, que é pior do que divergirem em palavra.
 */
import type {
  AppointmentStatus,
  BillingStatus,
  ChannelState,
  ClientStatus,
  ConversationStatus,
  DeliveryState,
  QualityRating,
  SlotCheck,
} from '@zenda/api-contract';
import type { Dictionary } from '../content/pt-BR';

export type Tone = 'blue' | 'pink' | 'mint' | 'amber' | 'rose' | 'slate';

export interface Labelled {
  readonly text: string;
  readonly tone: Tone;
}

/* ── Tons, independentes de idioma ────────────────────────────────────────── */

/* Ativo e o esperado de um cliente — verde nele pintava de conquista o estado
   normal, em toda linha da tabela e em todo cabecalho. Pausado e o desvio. */
const CLIENT_STATUS_TONE: Readonly<Record<ClientStatus, Tone>> = {
  active: 'mint',
  paused: 'amber',
  churned: 'slate',
};

/* `answered_by_doctor` guarda rosa de proposito: e a unica que conta um fato
   surpreendente — o medico respondeu pelo celular dele, por fora do Zenda, e a
   atendente precisa saber disso antes de responder de novo. Aberta e resolvida
   sao os dois estados normais de uma conversa. */
const CONVERSATION_TONE: Readonly<Record<ConversationStatus, Tone>> = {
  open: 'slate',
  pending: 'amber',
  answered_by_doctor: 'pink',
  resolved: 'slate',
};

/**
 * So o que da errado tem cor.
 *
 * Uma agenda de vinte consultas com azul, verde e cinza alternando vira faixa
 * listrada — e o olho para de distinguir justamente o que precisava saltar. Os
 * estados normais (pedida, agendada, confirmada, chegou, concluida) sao a
 * expectativa: cinza. Cancelada e falta sao o desvio, e ficam em rosa.
 *
 * `confirmed` foi o caso mais tentador de manter verde. Mas confirmada e o que
 * se ESPERA de uma consulta; pintar de verde e comemorar o normal, e o preco
 * disso e o "no-show" ao lado ficar um pouco menos visivel.
 */
const APPOINTMENT_TONE: Readonly<Record<AppointmentStatus, Tone>> = {
  requested: 'slate',
  scheduled: 'slate',
  confirmed: 'slate',
  checked_in: 'slate',
  completed: 'slate',
  cancelled: 'rose',
  no_show: 'rose',
};

/* `verifying` sai do cinza: nao e um estado neutro, e um estado de espera, e
   quem olha precisa saber que ainda nao da para enviar. */
const CHANNEL_TONE: Readonly<Record<ChannelState, Tone>> = {
  draft: 'slate',
  verifying: 'amber',
  active: 'mint',
  degraded: 'amber',
  inactive_warning: 'amber',
  disconnected: 'rose',
  suspended: 'rose',
  offboarded: 'slate',
};

/* O verde era cinza aqui, para nao comemorar o normal. Nao funcionou: numa
   grade de cinco fatos todos cinzas, nao havia como ler o estado sem ler o
   texto de cada um, e a cor deixava de informar qualquer coisa. Agora ela
   acompanha o estado. `UNKNOWN` fica cinza porque e ausencia de informacao, e
   nao um estado bom. */
const QUALITY_TONE: Readonly<Record<QualityRating, Tone>> = {
  GREEN: 'mint',
  YELLOW: 'amber',
  RED: 'rose',
  UNKNOWN: 'slate',
};

/* `missing_payment_method` e o estado silencioso em que o numero conecta e nao
   envia — o mais caro da lista, por isso rosa. */
const BILLING_TONE: Readonly<Record<BillingStatus, Tone>> = {
  ok: 'mint',
  unknown: 'slate',
  missing_payment_method: 'rose',
  blocked: 'rose',
};

/* ── Rótulos ──────────────────────────────────────────────────────────────── */

/**
 * Por que um horário da série não entra sozinho.
 *
 * Existia duplicado nas duas telas de marcação, como um `Record` literal em cada uma —
 * e uma delas ainda tinha o valor antigo `fora`, de antes do ADR 0020. Um veredito novo
 * do servidor aparecia numa tela e vazava cru na outra.
 *
 * `livre` não tem rótulo: quando está livre não há nada a dizer.
 */
export function slotReason(v: SlotCheck['reason']): string | null {
  switch (v) {
    case 'livre':
      return null;
    case 'ocupado':
      return 'ocupado';
    case 'deslocamento':
      return 'não dá tempo de chegar';
    case 'antecedencia':
      return 'muito em cima da hora';
    case 'fora_da_janela':
      return 'fora do atendimento';
    case 'estoura_a_janela':
      return 'passa do fim';
  }
}

export const clientStatus = (t: Dictionary, v: ClientStatus): Labelled => ({
  text: t.enums.clientStatus[v],
  tone: CLIENT_STATUS_TONE[v],
});

export const conversationStatus = (t: Dictionary, v: ConversationStatus): Labelled => ({
  text: t.enums.conversationStatus[v],
  tone: CONVERSATION_TONE[v],
});

export const appointmentStatus = (t: Dictionary, v: AppointmentStatus): Labelled => ({
  text: t.enums.appointmentStatus[v],
  tone: APPOINTMENT_TONE[v],
});

export const channelState = (t: Dictionary, v: ChannelState): Labelled => ({
  text: t.enums.channelState[v],
  tone: CHANNEL_TONE[v],
});

export const quality = (t: Dictionary, v: QualityRating): Labelled => ({
  text: t.enums.quality[v],
  tone: QUALITY_TONE[v],
});

/**
 * Versões curtas: numa grade de fatos o rótulo ao lado já diz do que se trata, e
 * repetir "Qualidade" dentro da etiqueta estoura a coluna.
 */
export const qualityShort = (t: Dictionary, v: QualityRating): Labelled => ({
  text: t.enums.qualityShort[v],
  tone: QUALITY_TONE[v],
});

export const billing = (t: Dictionary, v: BillingStatus): Labelled => ({
  text: t.enums.billing[v],
  tone: BILLING_TONE[v],
});

export const billingShort = (t: Dictionary, v: BillingStatus): Labelled => ({
  text: t.enums.billingShort[v],
  tone: BILLING_TONE[v],
});

export const modality = (t: Dictionary, v: 'in_person' | 'telehealth'): string =>
  t.enums.modality[v];

export const deliveryState = (t: Dictionary, v: DeliveryState): string => t.enums.delivery[v];

/**
 * Quem é atendido na consulta: o dependente, com o responsável ao lado, ou o próprio
 * paciente (ADR 0028). O responsável aparece porque é com ele que se fala.
 */
export function nomeDoAtendido(
  t: Dictionary,
  a: { readonly patientName: string; readonly dependentName: string | null },
): string {
  return a.dependentName === null
    ? a.patientName
    : t.patients.dependentes.deQuem(a.dependentName, a.patientName);
}
